'use server'

import { cookies } from 'next/headers'
import { revalidatePath } from 'next/cache'
import { commitTransaction, generatePayloadCookie, initTransaction, killTransaction, ValidationError } from 'payload'

import { getChildMetas, getCollectionMeta, getPayloadClient, requireSession } from './server'
import type { ActionResult, FieldErrors, FormState } from './types'
import type { ChildRows } from './types'
import { toPayloadData, ValueError } from './values'
import { getT } from '@/lib/i18n/server'
import { isLocale, LOCALE_COOKIE } from '@/lib/i18n/config'
import type { Translate, MessageKey } from '@/lib/i18n/translate'

const log = (event: string, detail: Record<string, unknown>) =>
  console.info(JSON.stringify({ scope: 'crud', event, ...detail }))

/** Map Payload / validation errors to a message plus per-field errors the form can display. */
function toFailure(error: unknown, t: Translate, prefix = ''): Extract<ActionResult, { ok: false }> {
  if (error instanceof ValueError) {
    const message = t(error.message as MessageKey)
    return { ok: false, message, fieldErrors: { [`${prefix}${error.path}`]: message } }
  }
  if (error instanceof ValidationError) {
    const fieldErrors: FieldErrors = {}
    const errors = (error.data as { errors?: { path: string; message: string }[] } | undefined)?.errors
    for (const item of errors ?? []) fieldErrors[`${prefix}${item.path}`] = item.message
    return { ok: false, message: t('err.fixFields'), fieldErrors }
  }
  const message = error instanceof Error ? error.message : t('err.unexpected')
  const status = (error as { status?: number }).status
  if (status === 403) return { ok: false, message: t('err.permission') }
  if (status === 404) return { ok: false, message: t('err.notFound') }
  return { ok: false, message }
}

export async function saveDocument(
  slug: string,
  id: string | number | null,
  state: FormState,
): Promise<ActionResult> {
  const { t } = await getT()
  try {
    const { payload, user } = await requireSession()
    const meta = await getCollectionMeta(slug, id === null ? 'create' : 'edit')
    if (id === null ? !meta.can.create : !meta.can.update) {
      return { ok: false, message: t('err.permission') }
    }
    const data = toPayloadData(meta.fields, state)
    if (id !== null && 'password' in data && !data.password) delete data.password

    const doc =
      id === null
        ? await payload.create({
            collection: slug as never,
            data: data as never,
            user,
            overrideAccess: false,
            depth: 0,
          })
        : await payload.update({
            collection: slug as never,
            id,
            data: data as never,
            user,
            overrideAccess: false,
            depth: 0,
          })
    log(id === null ? 'create' : 'update', { slug, id: (doc as { id: unknown }).id, user: user.id })
    revalidatePath(`/${slug}`, 'layout')
    revalidatePath('/')
    return { ok: true, id: (doc as { id: string | number }).id }
  } catch (error) {
    log('save-failed', { slug, id, error: error instanceof Error ? error.message : String(error) })
    return toFailure(error, t)
  }
}

export async function deleteDocument(slug: string, id: string | number): Promise<ActionResult> {
  const { t } = await getT()
  try {
    const { payload, user } = await requireSession()
    const meta = await getCollectionMeta(slug)
    if (!meta.can.delete) return { ok: false, message: t('err.permission') }
    await payload.delete({
      collection: slug as never,
      id,
      user,
      overrideAccess: false,
    })
    log('delete', { slug, id, user: user.id })
    revalidatePath(`/${slug}`, 'layout')
    revalidatePath('/')
    return { ok: true }
  } catch (error) {
    log('delete-failed', { slug, id, error: error instanceof Error ? error.message : String(error) })
    const failure = toFailure(error, t)
    // Foreign key violations are the common reason a delete fails; say so plainly.
    if (/foreign key|violates/i.test(failure.message)) {
      return { ok: false, message: t('err.referenced') }
    }
    return failure
  }
}

export async function loginAction(email: string, password: string): Promise<ActionResult> {
  const { t } = await getT()
  try {
    if (!email || !password) return { ok: false, message: 'Enter your email and password.' }
    const payload = await getPayloadClient()
    const result = await payload.login({ collection: 'users', data: { email, password } })
    if (!result.token) return { ok: false, message: t('err.loginFailed') }
    const collection = payload.collections.users.config
    const cookie = generatePayloadCookie({
      collectionAuthConfig: collection.auth,
      cookiePrefix: payload.config.cookiePrefix,
      token: result.token,
      returnCookieAsObject: true,
    })
    if (typeof cookie !== 'object') return { ok: false, message: t('err.loginFailed') }
    const jar = await cookies()
    jar.set(cookie.name, cookie.value ?? '', {
      httpOnly: true,
      path: '/',
      sameSite: 'lax',
      secure: cookie.secure,
      expires: cookie.expires ? new Date(cookie.expires) : undefined,
    })
    log('login', { user: result.user?.id })
    return { ok: true }
  } catch (error) {
    log('login-failed', { error: error instanceof Error ? error.message : String(error) })
    const status = (error as { status?: number }).status
    return {
      ok: false,
      message:
        status === 401 || status === 400 || /unauthor|incorrect|invalid/i.test(String(error))
          ? t('err.badCredentials')
          : /locked/i.test(String(error))
            ? t('err.locked')
            : t('err.signInRetry'),
    }
  }
}

export async function logoutAction(): Promise<void> {
  const payload = await getPayloadClient()
  const jar = await cookies()
  jar.delete(`${payload.config.cookiePrefix}-token`)
}

export type RelationOption = { value: string; label: string }

/** Typeahead source for relationship fields; respects the caller's read access. */
export async function searchRelation(
  slug: string,
  query: string,
  ids: string[] = [],
): Promise<RelationOption[]> {
  const { payload, user } = await requireSession()
  const collection = payload.collections[slug as keyof typeof payload.collections]?.config
  if (!collection) return []
  const titleField = collection.admin.useAsTitle || 'id'
  const where =
    ids.length > 0
      ? { id: { in: ids } }
      : query && titleField !== 'id'
        ? { [titleField]: { contains: query } }
        : undefined
  try {
    const result = await payload.find({
      collection: slug as never,
      where: where as never,
      limit: ids.length || 20,
      depth: 0,
      user,
      overrideAccess: false,
    })
    return result.docs.map((doc) => {
      const record = doc as unknown as Record<string, unknown>
      return { value: String(record.id), label: String(record[titleField] ?? record.id) }
    })
  } catch {
    return []
  }
}

/** Persist the UI language choice; the next render picks it up from the cookie. */
export async function setLocaleAction(locale: string): Promise<void> {
  if (!isLocale(locale)) return
  const jar = await cookies()
  jar.set(LOCALE_COOKIE, locale, { path: '/', maxAge: 60 * 60 * 24 * 365, sameSite: 'lax' })
  revalidatePath('/', 'layout')
}

/** Child fields always point at the parent; the form never supplies it. */
async function childData(childSlug: string, parentSlug: string, parentId: string | number, state: FormState) {
  const metas = await getChildMetas(parentSlug, 'edit')
  const meta = metas.find((item) => item.slug === childSlug)
  if (!meta) return null
  return { meta, data: { ...toPayloadData(meta.fields, state), [meta.on]: parentId } }
}

export async function saveChild(
  parentSlug: string,
  parentId: string | number,
  childSlug: string,
  id: string | number | null,
  state: FormState,
): Promise<ActionResult> {
  const { t } = await getT()
  try {
    const { payload, user } = await requireSession()
    const built = await childData(childSlug, parentSlug, parentId, state)
    if (!built || built.meta.readOnlyList) return { ok: false, message: t('err.notFound') }
    if (id === null ? !built.meta.can.create : !built.meta.can.update) return { ok: false, message: t('err.permission') }
    const doc =
      id === null
        ? await payload.create({ collection: childSlug as never, data: built.data as never, user, overrideAccess: false, depth: 0 })
        : await payload.update({ collection: childSlug as never, id, data: built.data as never, user, overrideAccess: false, depth: 0 })
    log(id === null ? 'child-create' : 'child-update', { childSlug, parentSlug, parentId, id: (doc as { id: unknown }).id, user: user.id })
    revalidatePath(`/${parentSlug}/${parentId}`)
    revalidatePath('/')
    return { ok: true, id: (doc as { id: string | number }).id }
  } catch (error) {
    log('child-save-failed', { childSlug, parentSlug, parentId, error: error instanceof Error ? error.message : String(error) })
    return toFailure(error, t)
  }
}

export async function deleteChild(
  parentSlug: string,
  parentId: string | number,
  childSlug: string,
  id: string | number,
): Promise<ActionResult> {
  const { t } = await getT()
  try {
    const { payload, user } = await requireSession()
    const meta = (await getChildMetas(parentSlug, 'edit')).find((item) => item.slug === childSlug)
    if (!meta || !meta.can.delete) return { ok: false, message: t('err.permission') }
    await payload.delete({ collection: childSlug as never, id, user, overrideAccess: false })
    log('child-delete', { childSlug, parentSlug, parentId, id, user: user.id })
    revalidatePath(`/${parentSlug}/${parentId}`)
    revalidatePath('/')
    return { ok: true }
  } catch (error) {
    log('child-delete-failed', { childSlug, parentSlug, parentId, id, error: error instanceof Error ? error.message : String(error) })
    return toFailure(error, t)
  }
}

/**
 * Create a parent and its child rows atomically: one transaction, so a bad row leaves nothing behind.
 * Field errors are keyed `children.<slug>.<row>.<field>` for the form to display next to the row.
 */
export async function createWithChildren(
  parentSlug: string,
  state: FormState,
  children: ChildRows,
): Promise<ActionResult> {
  const { t } = await getT()
  const { payload, user } = await requireSession()
  const parentMeta = await getCollectionMeta(parentSlug, 'create')
  if (!parentMeta.can.create) return { ok: false, message: t('err.permission') }
  const metas = await getChildMetas(parentSlug, 'create')

  // Validate shape of every row up-front (no writes yet) so errors point at the exact row.
  let parentData: Record<string, unknown>
  try {
    parentData = toPayloadData(parentMeta.fields, state)
  } catch (error) {
    return toFailure(error, t)
  }
  const prepared: { slug: string; rows: Record<string, unknown>[]; on: string }[] = []
  for (const [slug, rows] of Object.entries(children)) {
    const meta = metas.find((item) => item.slug === slug)
    if (!meta || !rows.length) continue
    if (!meta.can.create || meta.readOnlyList) return { ok: false, message: t('err.permission') }
    const built: Record<string, unknown>[] = []
    for (const [index, row] of rows.entries()) {
      try {
        built.push(toPayloadData(meta.fields, row))
      } catch (error) {
        return toFailure(error, t, `children.${slug}.${index}.`)
      }
    }
    prepared.push({ slug, rows: built, on: meta.on })
  }

  const req = { payload, user, context: {} } as Parameters<typeof initTransaction>[0]
  const started = await initTransaction(req)
  let where = 'parent'
  try {
    const parent = await payload.create({
      collection: parentSlug as never,
      data: parentData as never,
      user,
      overrideAccess: false,
      depth: 0,
      req,
    })
    const parentId = (parent as { id: string | number }).id
    for (const { slug, rows, on } of prepared) {
      for (const [index, data] of rows.entries()) {
        where = `children.${slug}.${index}.`
        await payload.create({
          collection: slug as never,
          data: { ...data, [on]: parentId } as never,
          user,
          overrideAccess: false,
          depth: 0,
          req,
        })
      }
    }
    if (started) await commitTransaction(req)
    log('create-with-children', { parentSlug, id: parentId, children: Object.fromEntries(prepared.map((p) => [p.slug, p.rows.length])), user: user.id })
    revalidatePath(`/${parentSlug}`, 'layout')
    revalidatePath('/')
    return { ok: true, id: parentId }
  } catch (error) {
    if (started) await killTransaction(req)
    log('create-with-children-failed', { parentSlug, where, error: error instanceof Error ? error.message : String(error) })
    return toFailure(error, t, where === 'parent' ? '' : where)
  }
}
